import { useState } from "react";
import { Link, useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { LogIn, Loader2, ShieldCheck } from "lucide-react";
import { login, getToken } from "../../lib/adminApi";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const [params] = useSearchParams();

  if (getToken()) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const res = await login(email, password, code);
      if (res?.mfa_required) {
        setMfaRequired(true);
        return;
      }
      nav("/admin");
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF8F3] flex items-center justify-center px-6">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="bg-white border border-[#E7E2D9] p-10 w-full max-w-md shadow-[0_24px_60px_-30px_rgba(17,17,17,0.18)]"
      >
        <img src="/logo.png" alt="Zarco's Decks" className="h-16 w-auto mb-8" />
        <h1 className="font-display text-3xl tracking-tight text-[#111]">Admin sign in</h1>
        <p className="mt-2 text-sm text-[#4A4A4A]">
          Manage content, photos, and incoming leads.
        </p>

        {params.get("reset") === "1" && (
          <p className="mt-4 text-sm text-green-700 bg-green-50 border border-green-200 px-3 py-2" data-testid="admin-reset-success">
            Password updated — sign in with your new password.
          </p>
        )}

        <label className="block mt-8 text-[11px] uppercase tracking-[0.2em] font-semibold">Email</label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="admin-email"
          className="mt-2 w-full border-b-2 border-[#E7E2D9] focus:border-[#99582A] py-3 outline-none"
        />
        <label className="block mt-6 text-[11px] uppercase tracking-[0.2em] font-semibold">Password</label>
        <input
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="admin-password"
          className="mt-2 w-full border-b-2 border-[#E7E2D9] focus:border-[#99582A] py-3 outline-none"
        />

        {mfaRequired && (
          <>
            <label className="block mt-6 text-[11px] uppercase tracking-[0.2em] font-semibold">
              Authentication code
            </label>
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              data-testid="admin-totp-code"
              className="mt-2 w-full border-b-2 border-[#E7E2D9] focus:border-[#99582A] py-3 outline-none tracking-[0.4em]"
            />
          </>
        )}

        {err && <p className="mt-4 text-sm text-red-600" data-testid="admin-error">{err}</p>}
        <button
          disabled={loading}
          data-testid="admin-login-btn"
          className="mt-8 w-full bg-[#111] hover:bg-[#99582A] transition-colors text-white py-4 font-semibold tracking-wide flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : mfaRequired ? <ShieldCheck size={16} /> : <LogIn size={16} />}
          {mfaRequired ? "Verify & sign in" : "Sign in"}
        </button>
        <div className="mt-5 text-center">
          <Link
            to="/admin/forgot-password"
            data-testid="admin-forgot-link"
            className="text-sm text-[#4A4A4A] hover:text-[#99582A]"
          >
            Forgot your password?
          </Link>
        </div>
      </form>
    </div>
  );
}
